import { Header, ReportBookProto, ReportPageProto } from "build-ts/metadata";
import showToast from "../hooks/useCustomToast";

/**
 * Creates or updates a report based on the provided Story object.
 *
 * @param {Story} story - The story object containing data for the report.
 * @returns {Promise<ReportBookProto>} A promise that resolves to the API response.
 * @throws {Error} Throws an error if the request fails or the environment variable is not set.
 */
export const createUpdateReport = async (story: ReportBookProto) => {
  try {
    const response = await fetch(process.env.REACT_APP_CREATE_UPDATE_REPORT!, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(ReportBookProto.toJSON(story)),
    });

    if (!response.ok) {
      const errorMsg = await response.text();
      showToast(`${errorMsg}`, "", "error");
    }

    const data = await response.json();
    return ReportBookProto.fromJSON(data);
  } catch (error) {
    showToast("Error creating/updating report", "", "error");
  }
};

/**
 * Creates or updates a report based on the provided Story object.
 *
 * @param {Page} page - The story object containing data for the report.
 * @returns {Promise<ReportPageProto>} A promise that resolves to the API response.
 * @throws {Error} Throws an error if the request fails or the environment variable is not set.
 */
export const createUpdatePage = async (page: ReportPageProto) => {
  try {
    const response = await fetch(process.env.REACT_APP_CREATE_UPDATE_PAGE!, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(ReportPageProto.toJSON(page)),
    });

    if (!response.ok) {
      const errorMsg = await response.text();
      showToast(`${errorMsg}`, "", "error");
    }

    const data = await response.json();
    return ReportPageProto.fromJSON(data);
  } catch (error) {
    showToast("Error creating/updating report", "", "error");
  }
};

/**
 * Creates or updates a report based on the provided Story object.
 *
 * @param {string} reportType - The story object containing data for the report.
 * @returns {Promise<Header[]>} A promise that resolves to the API response.
 * @throws {Error} Throws an error if the request fails or the environment variable is not set.
 */
export const getAllReports = async (reportType: string) => {
  try {
    const response = await fetch(`${process.env.REACT_APP_LIST_ALL_REPORT!}?reportType=${reportType}`, {
      method: "GET",
      credentials: "include",
    });

    if (!response.ok) {
      const errorMsg = await response.text();
      showToast(`${errorMsg}`, "", "error");
    }

    const data = await response.json();
    return data.map((header: any) => Header.fromJSON(header));
  } catch (error) {
    showToast("Error creating/updating report", "", "error");
  }
};

/**
 * Export story or pages.
 *
 * @param {string} reportType - report_page or report_book.
 * @param {string} id - id of page or report/story.
 * @returns {Promise<void>} A promise that resolves to the API response.
 * @throws {Error} Throws an error if the request fails or the environment variable is not set.
 */
export const exportContent = async (reportType: string, id: string) => {
  try {
    const response = await fetch(`${process.env.REACT_APP_EXPORT!}?reportType=${reportType}&reportId=${id}`, {
      method: "GET",
      credentials: "include",
    });

    if (!response.ok) {
      showToast("Error exporting", "", "error");
      return;
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.style.display = "none";
    a.href = url;
    a.download = `exported_${reportType}.pptx`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    showToast("PPT exported successfully.", "", "success");
  } catch (error) {
    showToast("Error exporting report", "", "error");
  }
};

/**
 * Get report.
 *
 * @param {string} id - id of report/story.
 * @returns {Promise<ReportBookProto>} A promise that resolves to the API response.
 * @throws {Error} Throws an error if the request fails or the environment variable is not set.
 */
export const getReport = async (id: string, reportType: string) => {
  try {
    const response = await fetch(`${process.env.REACT_APP_REPORT!}/${id}?reportType=${reportType}`, {
      method: "GET",
      credentials: "include",
    });

    if (!response.ok) {
      showToast("Error in getting Report book", "", "error");
      return null;
    }
    const data = await response.json();
    return ReportBookProto.fromJSON(data);
  } catch (error) {
    showToast("Error getting report", "", "error");
  }
};

/**
 * Delete story.
 *
 * @param {string} id - id of report/story.
 * @returns {Promise<void>} A promise that resolves to the API response.
 * @throws {Error} Throws an error if the request fails or the environment variable is not set.
 */
export const deleteReport = async (id: string, reportType: string) => {
  try {
    const response = await fetch(`${process.env.REACT_APP_REPORT!}/${id}?reportType=${reportType}`, {
      method: "DELETE",
      credentials: "include",
    });

    if (response.status !== 204) {
      showToast("Delete unsuccessful", "", "error");
    }

  } catch (error) {
    showToast("Error exporting report", "", "error");
  }
};
